import { FC } from 'react';
import theme from 'config/theme';
import { ENV_CONFIG } from 'config/environment';
import { Row } from 'components/Row';
import { Link } from 'components/Link';
import Spacing from 'components/Spacing';
import { Body } from 'components/Typography';
import Icon, { IconNames } from 'components/Icon';
import { GradientBorderCard } from 'components/Cards/GradientBorderCard';
import { abbreviateNum } from 'helpers/utils';
import { primaryDate, regularDate } from 'helpers/date';
import { IRecipient } from 'store/recipients/types';
import { HvpTag } from './HvpTag';
import { CardContent, StyledImage, TextDetails, Title, Description, CircleIcon } from './styled';

interface Props {
  openModal: (value: string) => void;
  recipient: IRecipient;
  isStandard?: boolean;
}

export const SummaryCard: FC<Props> = ({ openModal, recipient, isStandard }) => {
  const handleLink = () => {
    openModal(recipient.recipient_id);
  };

  return (
    <>
      <GradientBorderCard
        borderType="top"
        borderSize={4}
        gradient={theme.gradients.inkToDarkGradient({})}
      >
        <CardContent $justifyContent="space-between">
          <Row>
            <StyledImage
              alt={`Recipient ${recipient.recipient_name}`}
              src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/recipients/rec_${
                recipient.misc_ids?.photo_id
              }.jpg`}
              fallbackImgType="candidate_square"
            />
            <TextDetails>
              <Title>{recipient.recipient_name}</Title>
              <Description $isLight>
                {recipient.race?.election?.display_info}{' '}
                {recipient.race?.is_incumbent === null
                  ? '(Open)'
                  : !recipient.race?.is_incumbent
                    ? '(Challenger)'
                    : '(Incumbent)'}
              </Description>
              <Description $isLight>
                {recipient.race?.is_incumbent === null
                  ? 'vs'
                  : !recipient.race?.is_incumbent
                    ? 'Challenging Rep.'
                    : 'Being challenged by'}{' '}
                {recipient?.race?.election.competitor?.competitor_name}
              </Description>
              <Spacing $size={16} />
              <Description>
                ${abbreviateNum(parseFloat(recipient.race?.coh || '0'))} cash on hand as of{' '}
                {regularDate(recipient.race?.financials_updated_at)}
              </Description>
              <Row $alignItems="center" $pressable onClick={handleLink} whileTap={{ scale: 0.9 }}>
                <Link $color={theme.colors.jumbo} $align="left">
                  View more Info
                </Link>
                <Spacing $isVertical $size={4} />
                <CircleIcon>
                  <Icon name={IconNames.ChevronCircle} color={theme.colors.black} size={8} />
                </CircleIcon>
              </Row>
            </TextDetails>
          </Row>
          <TextDetails $align="flex-start">
            {recipient.impact_score && isStandard && (
              <>
                <Row $alignItems="center">
                  <Description $isLight $hiddenOnMobile>
                    Impact Score:
                  </Description>
                  <Spacing $isVertical $size={4} />
                  <Body $color={theme.colors.oathBlue}>
                    {recipient.impact_score && parseFloat(recipient.impact_score).toFixed(1)}
                  </Body>
                </Row>
                <Spacing $size={6} />
              </>
            )}
            <Row $alignItems="center">
              <Description $isLight $hiddenOnMobile>
                PVI:
              </Description>
              <Spacing $isVertical $size={4} />
              <HvpTag text={recipient?.race?.election.pvi || ''} />
            </Row>
            {recipient.race?.election.ratings?.length && (
              <>
                <Spacing $size={6} />
                <Row $alignItems="center">
                  <Description $isLight $hiddenOnMobile>
                    Race Rating:
                  </Description>
                  <Spacing $isVertical $size={4} />
                  <HvpTag text={recipient.race?.election.ratings[4] || ''} />
                </Row>
              </>
            )}
            {recipient.race?.election?.primary_election_date && (
              <>
                <Spacing $size={6} />
                <Row $alignItems="center">
                  <Description $isLight $hiddenOnMobile>
                    Primary Date:
                  </Description>
                  <Spacing $isVertical $size={4} />
                  <Description>
                    {primaryDate(recipient.race?.election?.primary_election_date)}
                  </Description>
                </Row>
              </>
            )}
          </TextDetails>
        </CardContent>
      </GradientBorderCard>
      <Spacing $size={10} />
    </>
  );
};
